import { Injectable } from '@angular/core';
import { DatabaseService } from './database.service';
import { HelperService } from './helper.service';
import { ProductFeature } from '../models';
import { StorageService } from './storage.service';
import { LocalAttachment } from './camera.service';

export interface IProductUpdate {
  target: "trainers" | "clients" | "all"
}

export interface WalkthroughCategory {
  id: string;
  order: number;
  title: string;
}

export interface WalkthroughPost {
  id: string;
  order: number;
  title: string;
  category: string;
  description: string;
  video: string;
  target?: "trainer" | "client";
}

@Injectable({
    providedIn: 'root'
})
  
export class SupportService {

  public howTo: any;

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
    private storageService: StorageService,
  ) { }

  async loadHowTo() {
    this.howTo = await this.dbService.getSnapshot("globalSettings/support/howTo");
  }

  getActivePost() {
    return this.dbService.getSnapshot('supportCenter/activePost');
  }

  getActiveTrainerPost() {
    return this.dbService.getSnapshot('supportCenter/activeTrainerPost');
  }

  // Product Features
  addProductFeature(feature: Partial<ProductFeature>) {
    return this.dbService.pushByRef('supportCenter/features', feature);
  }

  updateProductFeature(featureId: string, feature: Partial<ProductFeature>) {
    return this.dbService.updateRef(`supportCenter/features/${featureId}`, feature);
  }

  getProductFeatureList() {
    return this.dbService.getListByRefTest<any>(`supportCenter/features`);
  }

  async getProductFeature(featureId) {
    const data = await this.dbService.getSnapshot(`supportCenter/features/${featureId}`);
    return data? new ProductFeature({...data, id: featureId}) : null;
  }

  getProductFeatureObj(featureId) {
    return this.dbService.getObjectByRef(`supportCenter/features/${featureId}`, "id");
  }
  
  addProductFeatureVote(featureId: string, userId: string, data: {timestamp: string, userName: string}) {
    return this.dbService.updateRef(`supportCenter/features/${featureId}/votes/${userId}`, data);
  }

  async addProductFeatureVideo(featureId: string, attachment: LocalAttachment): Promise<string> {
    const storagePath = `support/features/${featureId}/${attachment.name}`;

    if(attachment.file) {
      return this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      return this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }
  }

  addProductFeatureComment(featureId: string, comment) {
    return this.dbService.pushByRef(`supportCenter/features/${featureId}/comments`, comment);
  }

  // Walkthrough

  async getWalkthroughCategories(): Promise<WalkthroughCategory[]> {
    const snap = await this.dbService.getSnapshot(`supportCenter/walkthrough/categories`);
    return this.helperService.transformFirebaseObjectToArray(snap);
  }

  addWalkthroughCategory(title: string) {
    return this.dbService.pushByRef(`supportCenter/walkthrough/categories`, {title: title});
  }

  updateWalkthroughCategory(categoryId: string, data: any) {
    return this.dbService.updateRef(`supportCenter/walkthrough/categories/${categoryId}`, data);
  }

  getWalkthroughPost(postId: string) {
    return this.dbService.getSnapshot(`supportCenter/walkthrough/posts/${postId}`);
  }

  async getWalkthroughPosts(): Promise<WalkthroughPost[]> {
    const snap = await this.dbService.getSnapshot(`supportCenter/walkthrough/posts`);
    return this.helperService.transformFirebaseObjectToArray(snap);
  }

  addWalkthroughPost(data: Partial<WalkthroughPost>) {
    return this.dbService.pushByRef(`supportCenter/walkthrough/posts`, data);
  }

  updateWalkthroughPost(postId: string, data: Partial<WalkthroughPost>) {
    return this.dbService.updateRef(`supportCenter/walkthrough/posts/${postId}`, data);
  }

  deleteWalkthroughPost(postId: string) {
    return this.dbService.removeRef(`supportCenter/walkthrough/posts/${postId}`);
  }
  
  async addWalkthroughPostVideo(attachment: LocalAttachment): Promise<any> {
    const storagePath = `support/walkthrough/${attachment.name}`;

    if(attachment.file) {
      return this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      return this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }
  }
}
